import clsx from 'clsx';
import { Button } from 'primereact/button';
import { DialogPassThroughOptions } from 'primereact/dialog';
import { type ReactNode } from 'react';
import { DialogSize } from '../constraints';
import { DialogButtonsConfig } from '../types';
import {
    DialogWithDoubleEscape,
    useDialogClose,
} from './DialogWithDoubleEscape';

interface ModalDialogProps {
    title?: ReactNode;
    size?: DialogSize;
    children: ReactNode;
    dialogClassName?: string;
    buttons?: DialogButtonsConfig;
    onClose: () => void;
    pt?: DialogPassThroughOptions;
}

export function ModalDialog({
    title,
    children,
    onClose,
    size,
    buttons,
    dialogClassName: propsDialogClassName,
    pt,
}: ModalDialogProps): ReactNode {
    const dialogClassName = clsx('dialog-form', size, propsDialogClassName);

    function HeaderContent(): ReactNode {
        const close = useDialogClose();
        const closeButtonLabel = buttons?.closeButton?.label ?? 'Close';
        const onCloseButtonClick = (): void => {
            buttons?.closeButton?.callback?.();
            if (!buttons?.closeButton?.overrideDefault) {
                close();
            }
        };

        return (
            <div className="flex flex-row justify-content-between">
                <div className="flex align-items-center">{title}</div>
                <Button
                    label={closeButtonLabel}
                    outlined
                    size="small"
                    onClick={onCloseButtonClick}
                />
            </div>
        );
    }

    function FooterContent(): ReactNode {
        const close = useDialogClose();
        const footerButtonTemplate = (
            <Button label="Close" severity="secondary" onClick={close} />
        );

        return (
            <div className="flex gap-2 justify-content-end">
                {buttons?.content ?? null}
                {!buttons?.content && footerButtonTemplate}
            </div>
        );
    }

    return (
        <DialogWithDoubleEscape
            onClose={onClose}
            className={dialogClassName}
            header={<HeaderContent />}
            footer={size !== DialogSize.sm ? undefined : <FooterContent />}
            pt={pt}
        >
            {children}
        </DialogWithDoubleEscape>
    );
}
